// Step2ReviewModal.tsx — opens after "Upload & review".
// Two clean steps, everything grouped BY FIELD so the SME reviews one field at a time:
//   Step 1 · New / unmatched fields — only the workbook fields with NO matching column; SME binds
//            each. (Existing/matched mappings are edited on the page, not here.)
//   Step 2 · Value changes   — per-field cards, each with its status changes + new values,
//            all pre-selected. SME collapses/expands, unticks exceptions, then Applies.
import React, { useEffect, useMemo, useState } from "react";
import {
  lookup_diffReference, lookup_applyReferenceDiff,
  type RefDiffResult, type RefDiffValueGroup,
} from "../api/lkpAPI";
import { useAuth } from "../hooks/useAuth";

const TONE: Record<string, string> = {
  Approved: "#2f7a4f", Obsolete: "#b23a30", Draft: "#b8860b",
  Candidate: "#43607e", "Submitted For Approval": "#6a5acd", "Not Documented Yet": "#8a97a3",
};
const pill = (s?: string) => (
  <span style={{ fontSize: 10, fontWeight: 600, color: "#fff", background: TONE[s || ""] || "#8a97a3", borderRadius: 20, padding: "1px 8px" }}>{s || "—"}</span>
);
const rowKey = (entity: string, code: string) => `${entity}::${code}`;

type Props = {
  appNm: string; entityId: string; refreshSignal: number;
  onClose: () => void; onApplied: () => void | Promise<void>;
};

export default function Step2ReviewModal(p: Props) {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [diff, setDiff] = useState<RefDiffResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  // per-row selection (default = selected). Keyed by entity::code, separately for status and values.
  const [unSel, setUnSel] = useState<Record<string, boolean>>({});   // true = UNticked
  const [openField, setOpenField] = useState<Record<string, boolean>>({});
  const [bindTo, setBindTo] = useState<Record<number, string>>({});
  const [selCols, setSelCols] = useState<Record<number, boolean>>({});

  const load = async () => {
    setLoading(true); setErr(null);
    try { setDiff(await lookup_diffReference(p.appNm, p.entityId)); }
    catch (e: any) { setErr(e?.detail || e?.message || "Could not compute changes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [p.appNm, p.entityId, p.refreshSignal]);

  const groups: RefDiffValueGroup[] = diff?.value_groups || [];
  const totalValueChanges = (diff?.summary.status_changes || 0) + (diff?.summary.new_values || 0);

  // selection helpers (default selected unless explicitly unticked)
  const isSel = (k: string) => !unSel[k];
  const toggle = (k: string) => setUnSel((m) => ({ ...m, [k]: !m[k] }));
  const setFieldAll = (g: RefDiffValueGroup, on: boolean) => {
    setUnSel((m) => {
      const next = { ...m };
      [...g.status_changes, ...g.new_values].forEach((r: any) => { next[rowKey(g.entity_id, r.code)] = !on; });
      return next;
    });
  };

  const selectedCount = useMemo(() => {
    let n = 0;
    groups.forEach((g) => [...g.status_changes, ...g.new_values].forEach((r: any) => { if (isSel(rowKey(g.entity_id, r.code))) n++; }));
    return n;
    /* eslint-disable-next-line */
  }, [groups, unSel]);

  // Descriptions are pure enrichment applied without ticking. They must still enable Apply —
  // otherwise, when there are no status/value changes left to review, the button stays disabled and
  // the descriptions never get written.
  const descCount = diff?.desc_backfill?.length || 0;
  const canApply = selectedCount > 0 || descCount > 0;

  const apply = async () => {
    if (!diff) return;
    setApplying(true); setErr(null);
    try {
      const status_changes = diff.status_changes.filter((c) => isSel(rowKey(c.entity_id, c.code)))
        .map((c) => ({ entity_id: c.entity_id, code: c.code, to_status: c.to_status }));
      const new_values = diff.new_values.filter((v) => isSel(rowKey(v.entity_id, v.code)))
        .map((v) => ({ entity_id: v.entity_id, code: v.code, display: v.display, governance_status: v.governance_status, description: v.description }));
      const new_columns = diff.new_columns.filter((_, i) => selCols[i]).map((c) => {
        const i = diff.new_columns.indexOf(c);
        const bind = (bindTo[i] || `${c.suggested_table || ""}.${c.suggested_column || ""}`).split(".");
        return { ...c, table: bind[0] || c.suggested_table, column: bind[1] || c.suggested_column };
      });
      const res = await lookup_applyReferenceDiff(p.entityId, user?.full_name || user?.email || "SME",
        { new_columns, status_changes, new_values,
          // descriptions are pure enrichment (no governance change) — always applied
          desc_backfill: diff.desc_backfill || [] });
      await p.onApplied();
      alert(`Applied — ${res.applied.status_changes} status changes, ${res.applied.new_values} new values, ${res.applied.new_columns} columns.`);
      p.onClose();
    } catch (e: any) { setErr(e?.detail || e?.message || "Apply failed"); }
    finally { setApplying(false); }
  };

  return (
    <div className="s2m-bg" onClick={p.onClose}>
      <style>{CSS}</style>
      <div className="s2m" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="s2m-h">
          <div className="s2m-title">Review uploaded reference</div>
          <button className="s2m-x" onClick={p.onClose}>×</button>
        </div>

        <div className="s2m-steps">
          <button className={"s2m-step" + (step === 1 ? " on" : "")} onClick={() => setStep(1)}>
            <span className="n">1</span> New / unmatched fields
            {diff && diff.summary.new_columns ? <span className="badge warn">{diff.summary.new_columns} to bind</span> : <span className="badge">0</span>}
          </button>
          <div className="s2m-line" />
          <button className={"s2m-step" + (step === 2 ? " on" : "")} onClick={() => setStep(2)}>
            <span className="n">2</span> Value changes
            {diff ? <span className="badge">{totalValueChanges}</span> : null}
          </button>
        </div>

        <div className="s2m-body">
          {loading ? <div className="s2m-muted">Comparing the uploaded workbook to your lookup…</div>
            : err ? <div className="s2m-err">{err}</div>
            : !diff ? null
            : step === 1 ? (
              diff.new_columns.length === 0 ? (
                <div className="s2m-ok">
                  ✓ Every field in the workbook matched an existing column.
                  <div className="s2m-okd">Nothing to bind — review the value changes in the next step. You can adjust existing mappings on the page anytime.</div>
                </div>
              ) : (
                <div className="s2m-bind-block">
                  <div className="s2m-sub">These workbook fields have no matching column in this app. Bind each to a physical column:</div>
                  {diff.new_columns.map((c, i) => (
                    <div key={i} className="s2m-bindrow">
                      <label className="s2m-bindhead">
                        <input type="checkbox" checked={!!selCols[i]} onChange={(e) => setSelCols({ ...selCols, [i]: e.target.checked })} />
                        <b>{c.logical_field || c.business_name}</b><span className="s2m-cnt">{c.value_count} values</span>
                      </label>
                      <div className="s2m-bindin">
                        <span>bind to:</span>
                        <input placeholder={`${c.suggested_table || "TABLE"}.${c.suggested_column || "COLUMN"}`}
                          value={bindTo[i] ?? (c.suggested_table && c.suggested_column ? `${c.suggested_table}.${c.suggested_column}` : "")}
                          onChange={(e) => setBindTo({ ...bindTo, [i]: e.target.value })} />
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              /* STEP 2 — value changes grouped by field */
              <>
                {groups.length === 0 ? (
                  <div className="s2m-muted">
                    No value or status changes in this upload.
                    {descCount > 0 && <div className="s2m-desc" style={{ marginTop: 6 }}>
                      {descCount} value description{descCount === 1 ? "" : "s"} from the workbook {descCount === 1 ? "is" : "are"} still missing — click
                      "Add {descCount} description{descCount === 1 ? "" : "s"}" below to write {descCount === 1 ? "it" : "them"}.
                    </div>}
                  </div>
                ) :
                  groups.map((g) => {
                    const open = openField[g.entity_id] ?? (groups.length <= 3);   // auto-open when few
                    const rows = [...g.status_changes.map((r: any) => ({ ...r, _kind: "status" })),
                                  ...g.new_values.map((r: any) => ({ ...r, _kind: "new" }))];
                    const selInField = rows.filter((r) => isSel(rowKey(g.entity_id, r.code))).length;
                    return (
                      <div key={g.entity_id} className="s2m-fld">
                        <div className="s2m-fldh" onClick={() => setOpenField({ ...openField, [g.entity_id]: !open })}>
                          <span className="s2m-caret">{open ? "▾" : "▸"}</span>
                          <b>{g.entity_name}</b>
                          {g.field_id ? <span className="s2m-fldcol">{g.field_id}</span> : null}
                          <span className="s2m-fldsub">{g.status_changes.length} status · {g.new_values.length} new</span>
                          <span className="s2m-fldsel">{selInField}/{rows.length} selected</span>
                          <button className="s2m-allbtn" onClick={(e) => { e.stopPropagation(); setFieldAll(g, selInField !== rows.length); }}>
                            {selInField === rows.length ? "Untick all" : "Tick all"}</button>
                        </div>
                        {open && (
                          <div className="s2m-rows">
                            {rows.map((r: any, j: number) => {
                              const k = rowKey(g.entity_id, r.code);
                              return (
                                <label key={j} className="s2m-vr">
                                  <input type="checkbox" checked={isSel(k)} onChange={() => toggle(k)} />
                                  <span className="s2m-code">{r.code}</span>
                                  {r.display ? <span className="s2m-disp">{r.display}</span> : null}
                                  <span className="s2m-change">
                                    {r._kind === "status"
                                      ? <>{pill(r.from_status)}<span className="s2m-arr">→</span>{pill(r.to_status)}</>
                                      : <>{pill(r.governance_status)}<span className="s2m-new">new</span></>}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </>
            )}
        </div>

        <div className="s2m-foot">
          {step === 2 && <button className="s2m-btn" onClick={() => setStep(1)}>← Back</button>}
          <span className="s2m-sp" />
          {descCount > 0 && <span className="s2m-desc">{descCount} value description{descCount === 1 ? "" : "s"} will be added</span>}
          {err && step !== 1 ? <span className="s2m-ferr">{err}</span> : null}
          {step === 1
            ? <button className="s2m-btn dark" onClick={() => setStep(2)}>Next: Value changes →</button>
            : <button className="s2m-btn go" onClick={apply} disabled={applying || !canApply}>
                {applying ? "Applying…"
                  : selectedCount > 0
                    ? `Apply ${selectedCount} change${selectedCount === 1 ? "" : "s"}${descCount ? ` + ${descCount} description${descCount === 1 ? "" : "s"}` : ""}`
                    : `Add ${descCount} description${descCount === 1 ? "" : "s"}`}</button>}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.s2m-bg{position:fixed;inset:0;background:rgba(14,36,54,.55);display:grid;place-items:center;z-index:90;padding:20px}
.s2m{background:#fff;border-radius:14px;width:min(760px,96vw);max-height:88vh;display:flex;flex-direction:column;box-shadow:0 24px 70px rgba(0,0,0,.35);overflow:hidden}
.s2m-h{display:flex;align-items:center;padding:15px 20px;background:#0e2436;color:#fff}
.s2m-title{font-size:16px;font-weight:600}
.s2m-x{all:unset;cursor:pointer;margin-left:auto;font-size:20px;color:#9fb4c8}
.s2m-steps{display:flex;align-items:center;gap:10px;padding:14px 20px;border-bottom:1px solid #eef2f6}
.s2m-step{all:unset;cursor:pointer;display:flex;align-items:center;gap:8px;font-size:13.5px;color:#8a97a3;font-weight:500}
.s2m-step.on{color:#0e2436}
.s2m-step .n{display:grid;place-items:center;width:22px;height:22px;border-radius:50%;background:#e3ebf0;color:#5f6e7d;font-size:12px;font-weight:600}
.s2m-step.on .n{background:#0e7c7b;color:#fff}
.s2m-line{flex:1;height:1px;background:#e3ebf0}
.s2m-step .badge{font-size:11px;font-family:var(--font-mono,monospace);background:#eef1f5;color:#5f6e7d;border-radius:20px;padding:1px 8px}
.s2m-step .badge.warn{background:#fcf6e9;color:#8a6d3b}
.s2m-body{padding:16px 20px;overflow-y:auto;flex:1}
.s2m-muted{color:#8a97a3;font-size:13px;padding:16px 0}
.s2m-err{color:#b23a30;font-size:13px;padding:12px;background:#fdf1f0;border:1px solid #f3d0cc;border-radius:8px}
.s2m-sub{font-size:12px;color:#8a6d3b;margin-bottom:8px;font-weight:500}
.s2m-bind-block{border:1px solid #ecdca8;background:#fdfaf2;border-radius:9px;padding:12px;margin-bottom:14px}
.s2m-bindrow{display:flex;flex-direction:column;gap:6px;padding:6px 0;border-bottom:1px solid #f2ead0}
.s2m-bindhead{display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px}
.s2m-cnt{font-size:11px;color:#8a97a3}
.s2m-bindin{display:flex;align-items:center;gap:8px;font-size:12px;color:#5f6e7d;padding-left:24px}
.s2m-bindin input{font-family:var(--font-mono,monospace);font-size:12px;padding:4px 8px;border:1px solid #dce3ea;border-radius:6px;width:220px}
.s2m-fld{border:1px solid #eef2f6;border-radius:9px;margin-bottom:8px;overflow:hidden}
.s2m-fldh{display:flex;align-items:center;gap:8px;padding:10px 12px;cursor:pointer;background:#f8fafb}
.s2m-fldh:hover{background:#f2f6f8}
.s2m-caret{color:#8a97a3;font-size:11px;width:12px}
.s2m-fldsub{font-size:11.5px;color:#8a97a3}
.s2m-fldcol{font-family:var(--font-mono,monospace);font-size:11px;color:#0e7c7b;background:#e0f3f1;border-radius:5px;padding:1px 7px}
.s2m-ok{font-size:14px;color:#2f7a4f;padding:20px 0;font-weight:500}
.s2m-okd{font-size:12.5px;color:#5f6e7d;font-weight:400;margin-top:6px}
.s2m-fldsel{margin-left:auto;font-size:11.5px;color:#5f6e7d;font-family:var(--font-mono,monospace)}
.s2m-allbtn{all:unset;cursor:pointer;font-size:11.5px;color:#0e7c7b;font-weight:500;border:1px solid #cbe7e2;border-radius:20px;padding:2px 10px}
.s2m-rows{padding:4px 12px 8px;max-height:280px;overflow-y:auto}
.s2m-vr{display:flex;align-items:center;gap:10px;padding:5px 0;font-size:12.5px;border-bottom:1px solid #f5f7f9}
.s2m-code{font-family:var(--font-mono,monospace);font-weight:600;min-width:60px}
.s2m-disp{color:#5f6e7d;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.s2m-change{display:flex;align-items:center;gap:6px;margin-left:auto}
.s2m-arr{color:#8a97a3}
.s2m-new{font-size:10.5px;color:#2f7a4f}
.s2m-foot{display:flex;align-items:center;gap:12px;padding:14px 20px;border-top:1px solid #eef2f6}
.s2m-sp{flex:1}
.s2m-ferr{color:#b23a30;font-size:12px}
.s2m-desc{font-size:12px;color:#0b5f5e}
.s2m-btn{all:unset;cursor:pointer;font-size:13px;padding:8px 16px;border:1px solid #dce3ea;border-radius:8px;color:#5f6e7d}
.s2m-btn.dark{background:#0e2436;color:#fff;border-color:#0e2436}
.s2m-btn.go{background:#0e7c7b;color:#fff;border-color:#0e7c7b;font-weight:600}
.s2m-btn:disabled{opacity:.5;cursor:default}
`;
